import { createContext, ReactNode, FC, useState, useCallback } from 'react';
import { ID } from '@/model/Common.model.ts';
import { WorkflowSetup, AlertState, CageAlertState } from '@/components/Workflow/Show/Workflow.model.ts';
import { _get, _isEmpty, _notNil } from '@/littledash.ts';
import { animalIdsRecord } from '@/constants/utils.ts';
import { Animal } from '@/model/Animal.model.ts';
import { Cage } from '@/model/Cage.model.ts';
import { AnimalAlertV1 } from '@/model/Alert.model.ts';
import { Measurement } from '@/model/Measurement.model.ts';
import { ApiErrorResponse } from '@/support/ApiService.ts';
import type { PercentageTypes } from '@/components/Subjects/Show/Measurements/Form/Form.utils.ts';

export type MeasurementsError = ApiErrorResponse<'GET /api/v1/animals/{animalId}/measurements'>;

export type ChangeAnimalCommandType =
  | { type: 'SELECT_ANIMAL'; animalId: ID }
  | { type: 'SELECT_ANIMAL_CAGE'; animalId: ID; cageId: ID }
  | { type: 'CLEAR_SELECTION' };
export type UpdateMeasurementsCommandType =
  | { type: 'SET_MEASUREMENTS'; animalId: ID; measurements: Array<Measurement> }
  | { type: 'SET_TODAYS_MEASUREMENT'; animalId: ID; measurement: Measurement | null }
  | { type: 'SET_MEASUREMENTS_LOADING'; animalId: ID; loading: boolean }
  | { type: 'SET_MEASUREMENTS_ERROR'; animalId: ID; error: MeasurementsError | null }
  | { type: 'CLEAR_MEASUREMENTS'; animalId?: ID }
  | { type: 'UPDATE_SINGLE_MEASUREMENT'; animalId: ID; measurement: Measurement };
export type ChangeCageCommandType = { type: 'SELECT_CAGE'; cageId: ID } | { type: 'MARK_DECEASED' };
export type SearchQueryCommandType = { searchQuery: string; collections: Array<Cage> };
export type UpdateAlertsCommandType =
  | { type: 'ADD_ALERTS'; newAlerts: Array<AnimalAlertV1> }
  | { type: 'RESOLVE_ALERT'; animalId: ID; alert: AnimalAlertV1 }
  | { type: 'RESET_ALERTS'; animalId: ID; fetchedAlerts: Array<AnimalAlertV1> }
  | { type: 'CLEAR_ALERTS' };

interface WorkflowContextResponse {
  selectedAnimal: ID | undefined;
  selectedCage: ID | 'deceased' | undefined;
  changeAnimal: (payload: ChangeAnimalCommandType) => void;
  changeCage: (payload: ChangeCageCommandType) => void;
  workflowSetup: WorkflowSetup | undefined;
  updateWorkflowSetup: (workflowSetup: WorkflowSetup) => void;
  originalSubjects: Array<Animal> | undefined;
  filteredSubjects: Array<Animal> | undefined;
  filteredCageIds: Array<ID> | undefined;
  updateOriginalSubjects: (originalSubjects: Array<Animal>) => void;
  updateSingleSubject: (updatedSubject: Animal) => void;
  handleSearchQuery: (payload: SearchQueryCommandType) => void;
  searchQuery: string | undefined;
  animalAlerts: AlertState | undefined;
  cageAlerts: CageAlertState | undefined;
  updateAlerts: (payload: UpdateAlertsCommandType) => void;
  animalMeasurements: Record<ID, Array<Measurement>> | undefined;
  todaysMeasurements: Record<ID, Measurement | null> | undefined;
  measurementsLoading: Record<ID, boolean> | undefined;
  measurementsErrors: Record<ID, MeasurementsError | null> | undefined;
  updateMeasurements: (payload: UpdateMeasurementsCommandType) => void;
  savedValueDifference: Record<ID, Record<string, PercentageTypes>> | undefined;
  updateSavedValueDifference: (animalId: ID, valueDifference: Record<string, PercentageTypes>) => void;
  clearSavedValueDifference: (animalId: ID) => void;
  clearAllSavedValueDifference: () => void;
}

export const WorkflowContext = createContext<WorkflowContextResponse>({
  selectedAnimal: undefined,
  selectedCage: undefined,
  changeAnimal: () => {},
  changeCage: () => {},
  workflowSetup: undefined,
  updateWorkflowSetup: () => {},
  originalSubjects: undefined,
  filteredSubjects: undefined,
  filteredCageIds: undefined,
  updateOriginalSubjects: () => undefined,
  updateSingleSubject: () => undefined,
  handleSearchQuery: () => {},
  searchQuery: undefined,
  animalAlerts: undefined,
  cageAlerts: undefined,
  updateAlerts: () => {},
  animalMeasurements: undefined,
  todaysMeasurements: undefined,
  measurementsLoading: undefined,
  measurementsErrors: undefined,
  savedValueDifference: undefined,
  updateMeasurements: () => {},
  updateSavedValueDifference: () => {},
  clearSavedValueDifference: () => {},
  clearAllSavedValueDifference: () => {},
});

const applySearchFilter = (entity: Animal | Cage, searchId: string, searchQuery: string) => {
  return (
    _get(entity, searchId)?.toLowerCase().includes(searchQuery) || _get(entity, 'catalog')?.toLowerCase() == searchQuery
  );
};

export const WorkflowProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [selectedAnimal, setSelectedAnimal] = useState<ID | undefined>();
  const [selectedCage, setSelectedCage] = useState<ID | 'deceased' | undefined>();
  const [workflowSetup, setWorkflowSetup] = useState<WorkflowSetup>();
  const [originalSubjects, setOriginalSubjects] = useState<Array<Animal> | undefined>();
  const [filteredSubjects, setFilteredSubjects] = useState<Array<Animal>>();
  const [filteredCageIds, setFilteredCageIds] = useState<Array<ID>>();
  const [searchQuery, setSearchQuery] = useState<string | undefined>();
  const [animalAlerts, setAnimalAlerts] = useState<AlertState>();
  const [cageAlerts, setCageAlerts] = useState<CageAlertState>();
  const [animalMeasurements, setAnimalMeasurements] = useState<Record<ID, Array<Measurement>> | undefined>();
  const [todaysMeasurements, setTodaysMeasurements] = useState<Record<ID, Measurement | null> | undefined>();
  const [measurementsLoading, setMeasurementsLoading] = useState<Record<ID, boolean> | undefined>();
  const [measurementsErrors, setMeasurementsErrors] = useState<Record<ID, MeasurementsError | null> | undefined>();
  const [savedValueDifference, setSavedValueDifference] = useState<
    Record<ID, Record<string, PercentageTypes>> | undefined
  >();

  const changeAnimal = useCallback(
    (payload: ChangeAnimalCommandType) => {
      switch (payload.type) {
        case 'SELECT_ANIMAL':
          setSelectedAnimal(payload.animalId);
          break;
        case 'SELECT_ANIMAL_CAGE':
          setSelectedAnimal(payload.animalId);
          setSelectedCage(payload.cageId);
          break;
        case 'CLEAR_SELECTION':
          setSelectedAnimal(undefined);
          setSelectedCage(undefined);
      }
    },
    [setSelectedAnimal, setSelectedCage]
  );

  const changeCage = useCallback(
    (payload: ChangeCageCommandType) => {
      setSelectedAnimal(undefined);
      switch (payload.type) {
        case 'SELECT_CAGE':
          setSelectedCage(payload.cageId);
          break;
        case 'MARK_DECEASED':
          setSelectedCage('deceased');
      }
    },
    [setSelectedAnimal, setSelectedCage]
  );

  const updateWorkflowSetup = useCallback(
    (workflowSetup: WorkflowSetup) => {
      setWorkflowSetup(workflowSetup);
    },
    [setWorkflowSetup]
  );

  const updateOriginalSubjects = useCallback(
    (originalSubjects: Array<Animal>) => {
      setOriginalSubjects(originalSubjects);
    },
    [setOriginalSubjects]
  );

  const updateSingleSubject = useCallback(
    (updatedSubject: Animal) => {
      const subjectIndex = filteredSubjects?.findIndex(({ id }) => id === updatedSubject.id);
      const originalSubjectIndex = originalSubjects?.findIndex(({ id }) => id === updatedSubject.id);

      if (_notNil(subjectIndex) && _notNil(filteredSubjects)) {
        const newFilteredSubjects = [...filteredSubjects];
        newFilteredSubjects[subjectIndex] = updatedSubject;
        setFilteredSubjects(newFilteredSubjects);
      }
      if (_notNil(originalSubjectIndex) && _notNil(originalSubjects)) {
        const newOriginalSubjects = [...originalSubjects];
        newOriginalSubjects[originalSubjectIndex] = updatedSubject;
        setOriginalSubjects(newOriginalSubjects);
      }
    },
    [setOriginalSubjects, setFilteredSubjects, originalSubjects, filteredSubjects]
  );

  const handleSearchQuery = useCallback(
    (payload: SearchQueryCommandType) => {
      changeAnimal({ type: 'CLEAR_SELECTION' });
      setSearchQuery(payload.searchQuery);

      if (_isEmpty(payload.searchQuery)) {
        setFilteredSubjects(undefined);
        setFilteredCageIds(undefined);
        return;
      }

      const filteredSubjects =
        originalSubjects?.filter((animal: Animal) => {
          const altRecordKey = animalIdsRecord[workflowSetup?.idToSearch ?? 'name'].animalIdKey;
          return applySearchFilter(animal, altRecordKey, payload.searchQuery.toLowerCase());
        }) ?? [];

      if (filteredSubjects.length == 0) {
        const filteredCages = payload.collections.filter((cage: Cage) => {
          return applySearchFilter(cage, 'name', payload.searchQuery.toLowerCase());
        });
        setFilteredSubjects(undefined);
        setFilteredCageIds(filteredCages.map((cage: Cage) => cage.id));
        if (filteredCages.length === 1) {
          changeCage({ type: 'SELECT_CAGE', cageId: filteredCages[0].id });
        }
      } else {
        setFilteredSubjects(filteredSubjects);
        setFilteredCageIds(undefined);
      }

      if (filteredSubjects.length === 1) {
        const onlySubject = filteredSubjects[0];
        changeAnimal({
          type: 'SELECT_ANIMAL_CAGE',
          animalId: onlySubject.id,
          cageId: onlySubject?.cage_id ?? '',
        });
      }
    },
    [setFilteredSubjects, setFilteredCageIds, changeAnimal, changeCage, workflowSetup, originalSubjects]
  );

  const updateAlerts = useCallback(
    (payload: UpdateAlertsCommandType) => {
      const processAlerts = (alerts: Array<AnimalAlertV1>, existingAnimalAlerts: AlertState | undefined) => {
        const updatedAnimalAlerts = alerts.reduce<AlertState>((acc, alert) => {
          if (_notNil(alert?.id) && _notNil(alert?.subject_id)) {
            const existingAnimalAlerts = { ...(acc?.[alert.subject_id] ?? {}) };
            if (existingAnimalAlerts[alert.id]) {
              delete existingAnimalAlerts[alert.id];
            }
            return {
              ...acc,
              [alert.subject_id]: { ...existingAnimalAlerts, [alert.id]: alert },
            };
          }
          return acc;
        }, existingAnimalAlerts ?? {});

        const updatedCageAlerts = alerts
          .filter(
            (alert) =>
              _notNil(alert?.id) &&
              (alert?.calculation === 'animal_food_intake' || alert?.calculation === 'animal_fluid_intake')
          )
          .reduce<CageAlertState>((acc, alert) => {
            const cageId = originalSubjects?.find((subject) => subject.id === alert.subject_id)?.cage_id;
            if (_notNil(cageId)) {
              return {
                ...acc,
                [cageId]: {
                  ...(acc?.[cageId] ?? {}),
                  [alert.id]: alert,
                },
              };
            }
            return acc;
          }, cageAlerts ?? {});

        return { updatedAnimalAlerts, updatedCageAlerts };
      };

      switch (payload.type) {
        case 'ADD_ALERTS': {
          const { updatedAnimalAlerts, updatedCageAlerts } = processAlerts(payload.newAlerts, animalAlerts);
          setAnimalAlerts(updatedAnimalAlerts);
          setCageAlerts(updatedCageAlerts);
          break;
        }
        case 'RESOLVE_ALERT': {
          if (animalAlerts?.[payload.animalId]?.[Number(payload.alert.id)]) {
            const updatedAnimalAlerts = { ...animalAlerts };
            updatedAnimalAlerts[payload.animalId][Number(payload.alert.id)] = {
              ...updatedAnimalAlerts[payload.animalId][Number(payload.alert.id)],
              resolved_at: payload.alert.resolved_at,
              resolved_by: payload.alert.resolved_by,
            };
            setAnimalAlerts(updatedAnimalAlerts);
          }
          const cageId = originalSubjects?.find((subject) => payload.animalId === subject.id)?.cage_id;
          if (_notNil(cageId) && cageAlerts?.[cageId]?.[Number(payload.alert.id)]) {
            const updatedCageAlerts = { ...cageAlerts };
            delete updatedCageAlerts[cageId][Number(payload.alert.id)];
            if (_isEmpty(updatedCageAlerts[cageId])) {
              delete updatedCageAlerts[cageId];
            }
            setCageAlerts(updatedCageAlerts);
          }
          break;
        }
        case 'RESET_ALERTS': {
          const existingAnimalAlerts = { ...animalAlerts };
          if (animalAlerts?.[payload.animalId]) {
            delete existingAnimalAlerts[payload.animalId];
          }
          const { updatedAnimalAlerts, updatedCageAlerts } = processAlerts(payload.fetchedAlerts, existingAnimalAlerts);
          setAnimalAlerts(updatedAnimalAlerts);
          setCageAlerts(updatedCageAlerts);
          break;
        }
        case 'CLEAR_ALERTS':
          setAnimalAlerts(undefined);
          setCageAlerts(undefined);
          break;
      }
    },
    [setAnimalAlerts, setCageAlerts, originalSubjects, animalAlerts, cageAlerts]
  );

  const updateMeasurements = useCallback((payload: UpdateMeasurementsCommandType) => {
    switch (payload.type) {
      case 'SET_MEASUREMENTS':
        setAnimalMeasurements((prev) => ({ ...(prev ?? {}), [payload.animalId]: payload.measurements }));
        break;
      case 'SET_TODAYS_MEASUREMENT':
        setTodaysMeasurements((prev) => ({ ...(prev ?? {}), [payload.animalId]: payload.measurement }));
        break;
      case 'SET_MEASUREMENTS_LOADING':
        setMeasurementsLoading((prev) => ({ ...(prev ?? {}), [payload.animalId]: payload.loading }));
        break;
      case 'SET_MEASUREMENTS_ERROR':
        setMeasurementsErrors((prev) => ({ ...(prev ?? {}), [payload.animalId]: payload.error }));
        break;
      case 'CLEAR_MEASUREMENTS':
        if (payload.animalId) {
          setAnimalMeasurements((prev) => {
            if (!prev) {
              return prev;
            }
            const { [payload.animalId as ID]: _, ...rest } = prev;
            return Object.keys(rest).length > 0 ? rest : undefined;
          });
        } else {
          setAnimalMeasurements(undefined);
        }
        break;
      case 'UPDATE_SINGLE_MEASUREMENT':
        setAnimalMeasurements((prev) => {
          if (!prev?.[payload.animalId]) {
            return prev;
          }
          return {
            ...prev,
            [payload.animalId]: [...prev[payload.animalId], payload.measurement],
          };
        });
        break;
    }
  }, []);

  const updateSavedValueDifference = useCallback((animalId: ID, valueDifference: Record<string, PercentageTypes>) => {
    setSavedValueDifference((prevState) => ({
      ...prevState,
      [animalId]: valueDifference,
    }));
  }, []);

  const clearSavedValueDifference = useCallback((animalId: ID) => {
    setSavedValueDifference((prevState) => {
      if (!prevState) {
        return prevState;
      }
      const { [animalId]: removed, ...rest } = prevState;
      return rest;
    });
  }, []);

  const clearAllSavedValueDifference = useCallback(() => {
    setSavedValueDifference(undefined);
  }, []);

  return (
    <WorkflowContext.Provider
      value={{
        selectedAnimal,
        selectedCage,
        changeAnimal,
        changeCage,
        workflowSetup,
        updateWorkflowSetup,
        originalSubjects,
        filteredSubjects,
        filteredCageIds,
        updateOriginalSubjects,
        updateSingleSubject,
        handleSearchQuery,
        searchQuery,
        animalAlerts,
        cageAlerts,
        updateAlerts,
        animalMeasurements,
        todaysMeasurements,
        measurementsLoading,
        measurementsErrors,
        updateMeasurements,
        savedValueDifference,
        updateSavedValueDifference,
        clearSavedValueDifference,
        clearAllSavedValueDifference,
      }}
    >
      {children}
    </WorkflowContext.Provider>
  );
};
